import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Layout as LayoutIcon, LogOut, ChevronDown, UserRound, Database, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { isPlainLeftClick } from '../../lib/navigation';
import { useLayout } from '../../context/LayoutContext';
import { useAuth } from '../../context/AuthContext';
import { useCapabilitiesRefetch } from '../../hooks/useCapabilitiesRefetch';
import Sidebar from './Sidebar';
import MarkdownModal from '../common/MarkdownModal';
import FeedbackWidget from '../common/FeedbackWidget';
import { FEATURE_TRIAL_ENABLED } from '../../constants/capabilities';

/** @deprecated kept for one-time migration only */
const TRIAL_BANNER_DISMISS_DATE_KEY = 'manteo_trial_banner_dismiss_date';
const TRIAL_WARNING_DISMISS_DATE_KEY = 'manteo_trial_warning_dismiss_date';
const TRIAL_EXPIRED_DISMISS_DATE_KEY = 'manteo_trial_expired_banner_dismiss_date';

function getLocalDateKey(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function readTrialDismissDates() {
  try {
    const legacy = localStorage.getItem(TRIAL_BANNER_DISMISS_DATE_KEY);
    const warning =
      localStorage.getItem(TRIAL_WARNING_DISMISS_DATE_KEY) ?? legacy ?? null;
    const expired = localStorage.getItem(TRIAL_EXPIRED_DISMISS_DATE_KEY) ?? null;
    return { warningDismissDate: warning, expiredDismissDate: expired };
  } catch {
    return { warningDismissDate: null, expiredDismissDate: null };
  }
}

const AppLayout = ({ children }) => {
  const { user, logout, capabilities } = useAuth();
  useCapabilitiesRefetch();
  const location = useLocation();
  const { headerContent, setHeaderContent } = useLayout();
  const isWorkspacePage = location.pathname.startsWith('/workspace/');
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [trialWarningDismissDate, setTrialWarningDismissDate] = useState(
    () => readTrialDismissDates().warningDismissDate
  );
  const [trialExpiredDismissDate, setTrialExpiredDismissDate] = useState(
    () => readTrialDismissDates().expiredDismissDate
  );
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const trialEndsAt = capabilities?.billing_period_end ? new Date(capabilities.billing_period_end) : null;
  const trialEnabled = !!capabilities?.features?.[FEATURE_TRIAL_ENABLED];
  const hasValidTrialEnd = trialEndsAt && !Number.isNaN(trialEndsAt.getTime());
  const showTrialBanner = trialEnabled && hasValidTrialEnd;
  const nowMs = Date.now();
  const endsInMs = hasValidTrialEnd ? trialEndsAt.getTime() - nowMs : null;
  const daysLeft = endsInMs == null ? null : Math.ceil(endsInMs / (1000 * 60 * 60 * 24));
  const isTrialExpired = daysLeft != null && daysLeft <= 0;
  const endsOnLabel = hasValidTrialEnd
    ? new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(trialEndsAt)
    : '';

  const todayKey = getLocalDateKey();
  const dismissDateForPhase = isTrialExpired ? trialExpiredDismissDate : trialWarningDismissDate;
  const showTrialOverlay = showTrialBanner && dismissDateForPhase !== todayKey;

  const dismissTrialOverlay = () => {
    const dateKey = getLocalDateKey();
    const end = capabilities?.billing_period_end ? new Date(capabilities.billing_period_end) : null;
    const validEnd = end && !Number.isNaN(end.getTime());
    const endsIn = validEnd ? end.getTime() - Date.now() : null;
    const daysLeftNow = endsIn == null ? null : Math.ceil(endsIn / (1000 * 60 * 60 * 24));
    const expiredNow = daysLeftNow != null && daysLeftNow <= 0;
    const storageKey = expiredNow ? TRIAL_EXPIRED_DISMISS_DATE_KEY : TRIAL_WARNING_DISMISS_DATE_KEY;
    try {
      localStorage.setItem(storageKey, dateKey);
    } catch {
      /* ignore quota / private mode */
    }
    if (expiredNow) {
      setTrialExpiredDismissDate(dateKey);
    } else {
      setTrialWarningDismissDate(dateKey);
    }
  };

  return (
    <div className="flex h-screen bg-white dark:bg-gray-950 transition-colors duration-300 overflow-hidden">
      {!isWorkspacePage && <Sidebar />}

      <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
        {/* Top User Bar - Only visible in Workspace */}
        {isWorkspacePage && (
          <header className="h-14 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950 flex items-center justify-between px-4 sticky top-0 z-[70] transition-colors shrink-0">
            <div className="flex items-center gap-4 flex-1 min-w-0 h-full">
              <Link
                to="/"
                onClick={(e) => {
                  if (!isPlainLeftClick(e)) return;
                  setHeaderContent(null);
                }}
                className="flex items-center group shrink-0 relative z-[60] p-0 ml-2"
                title="Go to Dashboard"
              >
                <div className="group-hover:scale-110 transition-transform">
                  <img src="/logo.svg" alt="manteo.ai" className="h-7" />
                </div>
              </Link>

              {headerContent && (
                <div className="flex-1 flex items-center h-full min-w-0">
                  {headerContent}
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className={cn(
                    "flex items-center gap-3 pl-2 pr-2 py-1 rounded-xl transition-all group border border-transparent",
                    isUserMenuOpen ? "bg-gray-50 dark:bg-gray-900 border-gray-100 dark:border-gray-800" : "hover:bg-gray-50 dark:hover:bg-gray-900 hover:border-gray-100 dark:border-gray-800"
                  )}
                >
                  <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-white font-black text-xs shadow-lg shadow-emerald-200/50 relative overflow-hidden">
                    {user?.email?.split('@')[0]?.substring(0, 2).toUpperCase() || 'GU'}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                  </div>
                  <ChevronDown size={14} className={cn("text-gray-400 transition-transform duration-200", isUserMenuOpen && "rotate-180")} />
                </button>

                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-950 border border-gray-100 dark:border-gray-800 rounded-2xl shadow-2xl py-2 z-[80] animate-in fade-in zoom-in-95 duration-100">
                    <div className="px-4 py-3 border-b border-gray-50 dark:border-gray-900/50">
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">Account</p>
                      <p className="text-xs font-bold text-gray-900 dark:text-white truncate">{user?.email || 'guest@example.com'}</p>
                    </div>

                    <div className="p-1">
                      <Link
                        to="/workspaces"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="flex items-center gap-3 px-3 py-2 text-xs font-bold text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-900 rounded-xl transition-colors uppercase tracking-tight"
                      >
                        <LayoutIcon size={16} />
                        Workspaces
                      </Link>
                      <Link
                        to="/sources"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="flex items-center gap-3 px-3 py-2 text-xs font-bold text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-900 rounded-xl transition-colors uppercase tracking-tight"
                      >
                        <Database size={16} />
                        Data Sources
                      </Link>
                      <div className="h-px bg-gray-50 dark:bg-gray-900/50 my-1 mx-2" />
                      <Link
                        to="/account"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="flex items-center gap-3 px-3 py-2 text-xs font-bold text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-900 rounded-xl transition-colors uppercase tracking-tight"
                      >
                        <UserRound size={16} />
                        Account
                      </Link>
                    </div>

                    <div className="px-1 border-t border-gray-50 dark:border-gray-900/50 mt-1 pt-1">
                      <button
                        onClick={() => {
                          logout();
                          setIsUserMenuOpen(false);
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2 text-xs font-bold text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 rounded-xl transition-colors uppercase tracking-tight"
                      >
                        <LogOut size={16} />
                        Logout
                      </button>
                    </div>

                    <div className="px-1 border-t border-gray-50 dark:border-gray-900/50 mt-1 pt-2">
                      <div className="px-3 py-2">
                        <p className="text-[14px] text-gray-400 dark:text-gray-500 text-center">
                          <button
                            onClick={() => {
                              setTermsModalOpen(true);
                              setIsUserMenuOpen(false);
                            }}
                            className="hover:text-gray-600 dark:hover:text-gray-400 transition-colors underline-offset-2 hover:underline"
                          >
                            Terms
                          </button>
                          {' · '}
                          <button
                            onClick={() => {
                              setPrivacyModalOpen(true);
                              setIsUserMenuOpen(false);
                            }}
                            className="hover:text-gray-600 dark:hover:text-gray-400 transition-colors underline-offset-2 hover:underline"
                          >
                            Privacy
                          </button>
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </header>
        )}

        <main className="relative flex-1 flex flex-col min-h-0 overflow-y-auto">
          <div className="absolute bottom-14 right-4 z-[55] pointer-events-none">
            <FeedbackWidget />
          </div>
          {showTrialOverlay && (
            <div
              className="absolute bottom-3 left-4 right-4 z-[55] pointer-events-none"
              role="region"
              aria-label="Trial notice"
            >
              <div
                className={cn(
                  'pointer-events-auto w-full rounded-lg px-3 py-2 text-[10px] leading-snug flex items-center gap-2 shadow-sm backdrop-blur-sm',
                  isTrialExpired
                    ? 'bg-red-50/50 dark:bg-red-950/40 text-red-900 dark:text-red-200'
                    : 'bg-amber-50/50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-200'
                )}
              >
                <button
                  type="button"
                  onClick={dismissTrialOverlay}
                  className={cn(
                    'shrink-0 rounded p-1 transition-colors',
                    isTrialExpired
                      ? 'hover:bg-red-100/70 dark:hover:bg-red-900/35 text-red-800 dark:text-red-100'
                      : 'hover:bg-amber-100/70 dark:hover:bg-amber-900/35 text-amber-900 dark:text-amber-100'
                  )}
                  aria-label="Close for today"
                  title="Close for today"
                >
                  <X size={14} strokeWidth={2.5} />
                </button>
                <div className="min-w-0 flex-1">
                  {isTrialExpired ? (
                    <>
                      <span className="font-black uppercase tracking-wide">Trial ended.</span>{' '}
                      Ended on <span className="font-bold">{endsOnLabel}</span>
                    </>
                  ) : (
                    <>
                      Trial period ends on <span className="font-bold">{endsOnLabel}</span>
                    </>
                  )}
                  {daysLeft != null && (
                    <span className="ml-1">
                      ({daysLeft > 0 ? `${daysLeft} day${daysLeft === 1 ? '' : 's'} left` : 'expired'})
                    </span>
                  )}
                </div>
                <Link
                  to="/account"
                  onClick={dismissTrialOverlay}
                  className={cn(
                    'shrink-0 inline-flex items-center rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide transition-colors',
                    isTrialExpired
                      ? 'hover:bg-red-100/70 dark:hover:bg-red-900/35'
                      : 'hover:bg-amber-100/70 dark:hover:bg-amber-900/35'
                  )}
                >
                  Review account
                </Link>
              </div>
            </div>
          )}
          {children}
        </main>

        <MarkdownModal
          isOpen={termsModalOpen}
          onClose={() => setTermsModalOpen(false)}
          markdownUrl="/terms-of-service.md"
        />

        <MarkdownModal
          isOpen={privacyModalOpen}
          onClose={() => setPrivacyModalOpen(false)}
          markdownUrl="/privacy-policy.md"
        />
      </div>
    </div>
  );
};

export default AppLayout;
